import React, { useState, useEffect, useCallback } from 'react';
import { Box, Alert, Button, CircularProgress, Divider, Paper, Typography } from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import BasicDetailsCard from './BasicDetailsCard';
import DateLocationCard from './DateLocationCard';
import FinalDetailsCard from './FinalDetailsCard';
import MsgModalBox from '../../../container/MessageModalBox/MsgModalBox';
import axios from 'axios';
import moment from 'moment';
import { APIURL } from '../../../config';
import { Redirect } from 'react-router-dom';

const SectionCard = ({ icon, title, children }) => (
  <Paper
    elevation={1}
    sx={{
      flex: '1 1 0',
      minWidth: 280,
      p: 2,
      borderRadius: 2,
      display: 'flex',
      flexDirection: 'column',
      bgcolor: '#ffffff',
      border: '1px solid #e0e7ef',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
      {icon}
      <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 600, color: '#2c3e50' }}>
        {title}
      </Typography>
    </Box>
    <Divider sx={{ mb: 1.5 }} />
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Paper>
);

const FIELD_LABELS = {
  pTitle: 'Project title',
  pDesc: 'Project description',
  active_plan_version: 'Project active plan version',
  pro_program_id: 'Program',
  pCustomer: 'Customer name',
  pManager: 'Project manager',
  pPrice: 'Price',
  pLoc: 'Location',
  selectedEmp: 'at least one employee',
  place01char: 'Place 01 char',
  place02char: 'Place 02 char',
  place03char: 'Place 03 char',
  place04date: 'Place 4 date',
  place05date: 'Place 5 date',
  place06number: 'Place 6 number',
  place07number: 'Place 7 number',
};

const ProjectForm_v2 = (props) => {
  const [schemaLocal, setSchemaLocal] = useState(props.schema);
  const [schemaDefaultsLocal, setSchemaDefaultsLocal] = useState(props.schemaDefaults);
  const getSchemaInfo = (field) => {
    const info = schemaLocal?.find((s) => s.columnName?.toLowerCase() === field.toLowerCase())
      || schemaDefaultsLocal?.find((s) => s.columnName?.toLowerCase() === field.toLowerCase())
      || {};
    return info;
  };
  const isFieldRequired = (field) => {
    const info = getSchemaInfo(field);
    return info.columnOptional !== undefined ? !info.columnOptional : false;
  };
  const isFieldVisible = (field) => {
    const info = getSchemaInfo(field);
    return info.columnVisible !== undefined ? !!info.columnVisible : true;
  };
  const getFieldLabel = (field, fallback) => getSchemaInfo(field).columnLabel || fallback;

  const [goHome, setGoHome] = useState(false);
  const [basicDetails, setBasicDetails] = useState({
    pTitle: props.edit ? props.project_details.pTitle : '',
    pDesc: props.edit ? props.project_details.pDesc : '',
    pro_program_id: props.edit ? (props.project_details.pro_program_id || '') : '',
    pCustomer: props.edit && props.project_details.pCustomer ? props.project_details.pCustomer : (props.customer_id || ''),
    pSponsor: props.edit ? (!props.project_details.pSponsor ? 'No Sponsor' : props.project_details.pSponsor) : 'No Sponsor',
    active_plan_version: props.edit ? (!!props.project_details.active_plan_version ? props.project_details.active_plan_version : '') : '',
    pManager: props.edit ? (!!props.project_details.pManager && props.empList.find((emp) => emp.id === props.project_details.pManager)?.emp_is_active) ? '' : props.project_details.pManager : '',
    pSelfPay: props.edit ? props.project_details.pSelfPay : false,
    pPrimaryPayer: props.edit ? props.project_details.pPrimaryPayer : '',
    pSecondaryPayer: props.edit ? props.project_details.pSecondaryPayer : '',
    place01char: props.edit ? props.project_details.place01char : '',
    place02char: props.edit ? props.project_details.place02char : '',
    place03char: props.edit ? props.project_details.place03char : '',
    place04date: props.edit ? props.project_details.place04date : '',
    place05date: props.edit ? props.project_details.place05date : '',
    place06number: props.edit ? props.project_details.place06number : '',
    place07number: props.edit ? props.project_details.place07number : '',
  });
  const [dateLocation, setDateLocation] = useState({
    pstrtDate: props.edit ? props.project_details.pstrtDate : moment().format('YYYY-MM-DDTHH:mm:ss'),
    pEndDate: props.edit ? props.project_details.pEndDate : moment().format('YYYY-MM-DDTHH:mm:ss'),
    pPrice: props.edit ? props.project_details.pPrice : 0,
    pCurrency: 'USD',
    pLoc: [],
  });
  const [finalDetails, setFinalDetails] = useState({ selectedEmp: [] });
  const [showModal, setShowModal] = useState(false);
  const [addError, setAddError] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [programs, setPrograms] = useState([]);
  const [programsLoading, setProgramsLoading] = useState(true);
  const [programsError, setProgramsError] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [placeHolderVisStatus, setPlaceHolderVisStatus] = useState({
    place01char: false,
    place02char: false,
    place03char: false,
    place04date: false,
    place05date: false,
    place06number: false,
    place07number: false,
  });

  useEffect(() => {
    const getFieldPropertiesFromSchema = (field, schema, defSchema) => {
      const info = schema?.data?.find((s) => s.columnName?.toLowerCase() === field.toLowerCase())
        || defSchema?.data?.find((s) => s.columnName?.toLowerCase() === field.toLowerCase())
        || {};
      return {
        isVisible: info.columnVisible !== undefined ? !!info.columnVisible : true,
        isRequired: info.columnOptional !== undefined ? !info.columnOptional : false,
      };
    };

    const fetchSchema = async () => {
      try {
        const [schRes, defRes] = await Promise.all([
          axios.get(`${APIURL}/schema/table/project`, { params: { companyID: props.comp_id } }),
          axios.get(`${APIURL}/schema/table-default/project`, {
            params: {
              companyState: props.user.comp_settings.find((s) => s.feature_id === 99)?.state_id || null,
              companyTypeOfBusiness: props.user.comp_settings.find((s) => s.feature_value === 'primaryBusinessType')?.feature_id || null,
            },
          }),
        ]);
        setSchemaLocal(schRes.data);
        setSchemaDefaultsLocal(defRes.data);
        setPlaceHolderVisStatus(Object.fromEntries(Object.entries(placeHolderVisStatus).map(([key]) => [key, getFieldPropertiesFromSchema(key, schRes, defRes).isVisible])));
        setIsLoading(false);
      } catch (err) {
        console.error('Failed to fetch schema for ProjectForm', err);
        setIsLoading(false);
      }
    };
    if (!props.schema || !props.schemaDefaults) {
      fetchSchema();
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const response = await axios.get(`${APIURL}/programs`, {
          params: { company_id: props.comp_id },
        });
        const programList = Array.isArray(response.data) ? response.data : [];
        setPrograms(programList);

        if (!props.edit && programList.length === 1) {
          setBasicDetails((current) => ({
            ...current,
            pro_program_id: programList[0].program_id,
          }));
        }
      } catch (err) {
        console.error('Failed to fetch programs for ProjectForm_v2', err);
        setProgramsError('Unable to load programs.');
      } finally {
        setProgramsLoading(false);
      }
    };

    fetchPrograms();
  }, [props.comp_id, props.edit]);

  useEffect(() => {
    axios.defaults.headers = {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Headers': '*',
      'Access-Control-Allow-Methods': 'PUT, GET, POST, DELETE, OPTIONS',
      'ACCESS-CONTROL-ALLOW-CREDENTIAL': true,
      Authorization: 'Bearer ' + window.localStorage.getItem('Fw4_access_Token'),
    };
    if (props.edit) {
      props.project_details.pLoc.forEach((loc) => {
        handleMultipleLoc({ target: { value: loc.id } });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addProjectToDB = useCallback(async (e) => {
    const newState = {
      basicDetails: {
        pTitle: '',
        pDesc: '',
        pro_program_id: programs.length === 1 ? programs[0].program_id : '',
        pCustomer: '',
        pSponsor: 'No Sponsor',
        active_plan_version: '',
        pManager: '',
      },
      dateLocation: { pstrtDate: moment().format('YYYY-MM-DDTHH:mm:ss'), pEndDate: moment().format('YYYY-MM-DDTHH:mm:ss'), pPrice: '', pCurrency: 'USD', pLoc: [] },
      finalDetails: { selectedEmp: [] },
    };
    setIsSubmitting(true);
    e.preventDefault();
    e.persist();
    var data = {
      company_id: props.comp_id,
      user_id: props.user.emp_id,
      ddd_id: props.edit
        ? props.project_details.ddd_id
        : props.customerList.find((cus) => cus.id === +basicDetails.pCustomer)?.ddd_id || null,
      active_plan_version: basicDetails.active_plan_version,
      pro_program_id: basicDetails.pro_program_id ? Number(basicDetails.pro_program_id) : null,
      projId: !props.edit ? null : props.project_details.projId,
      pTitle: basicDetails.pTitle,
      pDesc: basicDetails.pDesc,
      pCustomer: parseInt(basicDetails.pCustomer),
      pSponsor: basicDetails.pSponsor === 'No Sponsor' ? 'NULL' : basicDetails.pSponsor,
      pManager: parseInt(basicDetails.pManager),
      pro_self_pay: basicDetails.pSelfPay || false,
      pro_primary_payer: parseInt(basicDetails.pPrimaryPayer),
      pro_secondary_payer: parseInt(basicDetails.pSecondaryPayer),
      pstrtDate: dateLocation.pstrtDate,
      pEndDate: dateLocation.pEndDate,
      pPrice: dateLocation.pPrice,
      pCurrency: dateLocation.pCurrency,
      pLoc: dateLocation.pLoc,
      selectedEmp: props.edit ? [] : finalDetails.selectedEmp,
      place01char: basicDetails.place01char || null,
      place02char: basicDetails.place02char || null,
      place03char: basicDetails.place03char || null,
      place04date: basicDetails.place04date || null,
      place05date: basicDetails.place05date || null,
      place06number: basicDetails.place06number || null,
      place07number: basicDetails.place07number || null,
    };
    let msg = null;
    if (props.edit) {
      msg = await axios.put(APIURL + '/project', data);
    } else {
      msg = await axios.post(APIURL + '/project', data);
    }
    if (msg.data.code === 200) {
      if (props?.updateProject) {
        props.updateProject();
        setShowModal(false);
      } else {
        setShowModal(true);
      }
      setBasicDetails(newState.basicDetails);
      setDateLocation(newState.dateLocation);
      setFinalDetails(newState.finalDetails);
      setSubmitAttempted(false);
      setAddError(false);
    } else {
      setAddError(true);
      setShowModal(true);
    }
    setIsSubmitting(false);
  }, [props, basicDetails, dateLocation, finalDetails, programs]);

  const goHomeHandler = useCallback(() => {
    setAddError(false);
    setShowModal(false);
    setShowAlert(false);
    setGoHome(true);
  }, []);

  const addNew = useCallback(() => {
    if (addError) {
      setShowModal(false);
      setShowAlert(false);
      if (props.edit) props.history.push('/projects');
    } else {
      setShowModal(false);
      setShowAlert(false);
      setAddError(false);
      setSubmitAttempted(false);
    }
  }, [addError, props]);

  const validateAll = () => {
    const sections = props.edit ? [basicDetails, dateLocation] : [basicDetails, dateLocation, finalDetails];
    for (const section of sections) {
      const validation = props.isFormValidated(section);
      if (!validation.isValidated) {
        setErrorMsg('Please enter ' + (FIELD_LABELS[validation.missingKey] || 'all required fields'));
        setShowAlert(true);
        return false;
      }
    }
    // isFormValidated skips the schema-driven place* fields, so block on any required+visible ones here.
    const placeFields = ['place01char', 'place02char', 'place03char', 'place04date', 'place05date', 'place06number', 'place07number'];
    for (const f of placeFields) {
      if (placeHolderVisStatus[f] && isFieldRequired(f) && !basicDetails[f]) {
        setErrorMsg('Please enter ' + getFieldLabel(f, FIELD_LABELS[f] || f));
        setShowAlert(true);
        return false;
      }
    }
    if (moment(dateLocation.pEndDate).isBefore(moment(dateLocation.pstrtDate))) {
      setErrorMsg('End Date must be after Start Date.');
      setShowAlert(true);
      return false;
    }
    setShowAlert(false);
    setErrorMsg('');
    return true;
  };

  const handlebasicDetailsChange = useCallback((e) => {
    let newState = { ...basicDetails, [e.target.id]: e.target.value };
    if (e.target.id === 'pManager') {
      handleSelectedEmp(e);
    }
    setBasicDetails(newState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicDetails]);

  const handledateLocationChange = useCallback((e) => {
    let dateLoc = { ...dateLocation };
    if (e.target.id === 'pstrtDate') {
      if (moment(e.target.value).isBefore(moment().subtract(1, 'd'))) {
        dateLoc.pstrtDate = moment().format('YYYY-MM-DDTHH:mm:ss');
        dateLoc.pEndDate = moment().format('YYYY-MM-DDTHH:mm:ss');
        setDateLocation(dateLoc);
        setErrorMsg(`Please Enter Start Date after ${moment().subtract(1, 'd').format('DD MMM YYYY')}`);
        setShowAlert(true);
        return;
      } else {
        dateLoc.pstrtDate = e.target.value;
      }
    } else if (e.target.id === 'pEndDate') {
      if (!dateLoc.pstrtDate) {
        dateLoc.pEndDate = moment().format('YYYY-MM-DDTHH:mm:ss');
        setDateLocation(dateLoc);
        setErrorMsg('Please Enter Start Date First');
        setShowAlert(true);
        return;
      } else if (dateLoc.pstrtDate && moment(e.target.value).isBefore(moment(dateLoc.pstrtDate))) {
        dateLoc.pEndDate = moment().format('YYYY-MM-DDTHH:mm:ss');
        setDateLocation(dateLoc);
        setErrorMsg(`Please Enter Valid End Date after ${moment(dateLoc.pstrtDate).subtract(1, 'd').format('DD MMM YYYY')}`);
        setShowAlert(true);
        return;
      } else {
        dateLoc.pEndDate = e.target.value;
      }
    } else {
      dateLoc = { ...dateLocation, [e.target.id]: e.target.value };
    }
    setErrorMsg('');
    setShowAlert(false);
    setDateLocation(dateLoc);
  }, [dateLocation]);

  const handleSelectedEmp = useCallback((e) => {
    let chosenEmpId = e.target.value;
    let chosenEmp = props.empList.find(({ id }) => id === +chosenEmpId);
    let empExist = finalDetails.selectedEmp.find(({ empId }) => empId === +chosenEmpId);
    if (e.target.id === 'pEmp') {
      e.target.value = '';
    }
    if (!empExist && chosenEmpId !== '') {
      setFinalDetails((prev) => {
        const newEmp = {
          empName: chosenEmp.employee_name,
          empId: chosenEmp.id,
          pEmpStatus: '',
          pEmpBillRate: '',
          pEmpCurrency: 'USD',
          pEmpFrequency: '',
          pEmpAssFromDate: moment().format('YYYY-MM-DDTHH:mm:ss'),
          pEmpAssToDate: moment().format('YYYY-MM-DDTHH:mm:ss'),
        };
        if (e.target.id === 'pManager' && basicDetails.pManager !== '') {
          let selectedEmp = prev.selectedEmp.filter((em) => em.empId !== basicDetails.pManager);
          selectedEmp.push(newEmp);
          return { ...prev, selectedEmp };
        } else {
          return { ...prev, selectedEmp: [...prev.selectedEmp, newEmp] };
        }
      });
    } else {
      if (e.target.id === 'pEmp') {
        alert('Already Exist');
      }
    }
  }, [props.empList, finalDetails.selectedEmp, basicDetails.pManager]);

  const handleMultipleLoc = useCallback((e) => {
    let oldState = { ...dateLocation };
    let chosenLocId = e.target.value;
    let chosenLoc = props.locList.find(({ id }) => id === +chosenLocId);
    let LocExist = oldState.pLoc.find(({ locId }) => locId === +chosenLocId);
    e.target.value = '';
    if (!LocExist) {
      const newLoc = {
        locId: chosenLoc.id,
        location_addr1: chosenLoc.location_addr1,
        location_addr2: chosenLoc.location_addr2,
        location_city: chosenLoc.location_city,
        location_state: chosenLoc.location_state,
        location_zip: chosenLoc.location_zip,
        location_lat: chosenLoc.location_lat,
        location_lon: chosenLoc.location_lon,
        location_type: chosenLoc.location_type,
        location_status: chosenLoc.location_status,
        location_county: chosenLoc.location_county,
      };
      oldState.pLoc.push(newLoc);
      setDateLocation(oldState);
    } else {
      alert('Already Exist');
    }
  }, [dateLocation, props.locList]);

  const onLocDelete = useCallback((id) => {
    const pLoc = dateLocation.pLoc.filter((loc) => loc.locId !== id);
    setDateLocation({ ...dateLocation, pLoc });
  }, [dateLocation]);

  const handleChangeEmp = useCallback((id, evt) => {
    setFinalDetails((prevState) => {
      let empDets = prevState.selectedEmp.find((employee) => employee.empId === id);
      let selectedEmp = prevState.selectedEmp.map((emp) => {
        if (emp.empId === id) {
          if (evt.target.id === 'pEmpAssFromDate') {
            if (moment(evt.target.value).isBetween(moment(dateLocation.pstrtDate).subtract(1, 'd'), moment(dateLocation.pEndDate).add(1, 'd'))) {
              emp[evt.target.id] = evt.target.value;
              setErrorMsg('');
              setShowAlert(false);
            } else {
              emp[evt.target.id] = '';
              setErrorMsg(`Please Enter Valid Date Between ${moment(dateLocation.pstrtDate).format('MMM DD YYYY')} - ${moment(dateLocation.pEndDate).format('MMM DD YYYY')}`);
              setShowAlert(true);
            }
          } else if (evt.target.id === 'pEmpAssToDate') {
            if (!empDets.pEmpAssFromDate) {
              emp[evt.target.id] = '';
              setErrorMsg('Please Enter Associate From Field First');
              setShowAlert(true);
            } else if (empDets.pEmpAssFromDate && !moment(evt.target.value).isBetween(moment(empDets.pEmpAssFromDate).subtract(1, 'd'), moment(dateLocation.pEndDate).add(1, 'd'))) {
              emp[evt.target.id] = '';
              setErrorMsg(`Please Enter Associate To Field in range ${moment(empDets.pEmpAssFromDate).format('MMM DD YYYY')} - ${moment(dateLocation.pEndDate).format('MMM DD YYYY')}`);
              setShowAlert(true);
            } else {
              emp[evt.target.id] = evt.target.value;
              setErrorMsg('');
              setShowAlert(false);
            }
          } else {
            emp[evt.target.id] = evt.target.value;
          }
        }
        return emp;
      });
      return { ...prevState, selectedEmp };
    });
  }, [dateLocation]);

  const onDelete = useCallback((id) => {
    setFinalDetails((prevState) => {
      const selectedEmp = prevState.selectedEmp.filter((e) => e.empId !== id);
      return { ...prevState, selectedEmp };
    });
  }, []);

  if (goHome) return <Redirect to="/" />;

  return (
    <Box sx={{ textAlign: 'left', px: 1 }}>
      {showModal ? (
        <MsgModalBox
          edit={props.edit}
          addNew={addNew}
          goHome={goHomeHandler}
          text="Project"
          showError={addError}
          style={{ position: 'absolute' }}
          taskDates={[]}
        />
      ) : isLoading || programsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          component="form"
          noValidate
          onSubmit={(e) => {
            setSubmitAttempted(true);
            if (validateAll()) {
              addProjectToDB(e);
            } else {
              e.preventDefault();
            }
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1f3a5f', mb: 1 }}>
            {props.edit ? 'Edit Project' : (props.customerPage ? '' : 'New Project')}
          </Typography>

          {showAlert && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMsg || 'Please fill out missing form fields'}
            </Alert>
          )}
          {programsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {programsError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 2, alignItems: 'stretch' }}>
            <SectionCard icon={<AssignmentOutlinedIcon sx={{ color: '#1976d2' }} />} title="Basic Details">
              <BasicDetailsCard
                edit={props.edit}
                handlebasicDetailsChange={handlebasicDetailsChange}
                formVals={basicDetails}
                customerList={props.customerList}
                empList={props.empList}
                proj_employees={props.proj_Employees}
                contactList={props.contactList}
                payerList={props.payerList}
                programs={programs}
                getFieldLabel={getFieldLabel}
                isFieldRequired={isFieldRequired}
                isFieldVisible={isFieldVisible}
                visibilityStatus={placeHolderVisStatus}
                submitAttempted={submitAttempted}
              />
            </SectionCard>

            <SectionCard icon={<EventOutlinedIcon sx={{ color: '#1976d2' }} />} title="Date & Location">
              <DateLocationCard
                edit={props.edit}
                locList={props.locList}
                formVals={dateLocation}
                mapRender={props.mapRender}
                handledateLocationChange={handledateLocationChange}
                handleMultipleLoc={handleMultipleLoc}
                onLocDelete={onLocDelete}
                pLocations={dateLocation.pLoc}
                taskList={props.edit ? props.taskList : null}
                submitAttempted={submitAttempted}
              />
            </SectionCard>

            {!props.edit && (
              <SectionCard icon={<GroupOutlinedIcon sx={{ color: '#1976d2' }} />} title="Final Details">
                <FinalDetailsCard
                  handleSelectedEmp={handleSelectedEmp}
                  handleChangeEmp={handleChangeEmp}
                  onDelete={onDelete}
                  formVals={finalDetails}
                  empStatus={props.empStatus}
                  frequency={props.frequency.map((fre) => fre.feature_value)}
                  empList={props.empList}
                  pManagerId={basicDetails.pManager}
                  selectedEmp={finalDetails.selectedEmp}
                  submitAttempted={submitAttempted}
                />
              </SectionCard>
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {isSubmitting ? (props.edit ? 'Saving Project...' : 'Creating Project...') : (props.edit ? 'Save Project' : 'Create Project')}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ProjectForm_v2;
